import { Tooltip } from 'antd';
import { FC, ReactNode } from 'react';

// Helper to measure text width using canvas
const measureTextWidth = (
  text: string,
  fontSize: number,
  fontWeight = 'normal'
): number => {
  if (typeof document === 'undefined') return text.length * fontSize * 0.6; // SSR fallback
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return text.length * fontSize * 0.6;
  context.font = `${fontWeight} ${fontSize}px sans-serif`;
  return context.measureText(text).width;
};

export interface IRadialBarData {
  name: string;
  label: string;
  count: number;
  color: string;
  /** Optional value used for bar length calculation (if different from count) */
  barValue?: number;
}

/** Configuration for clickable legend items */
export interface ILegendClickConfig {
  /** Handler called when the legend count is clicked */
  onClick: (name: string) => void;
  /** Tooltip content shown on hover */
  tooltip: ReactNode;
}

interface IRadialBarChartProps {
  data: IRadialBarData[];
  maxValue?: number;
  height?: number;
  /** @deprecated Use spread and fill instead */
  barWidth?: number;
  /** Gap between chart and legend (default: 10) */
  gap?: number;
  /** Minimum inner radius (hole size). If not set, calculated automatically. */
  innerRadius?: number;
  /** Distance between each row (number/label/bar). Higher = more spread out. */
  spread?: number;
  /** How much of the spread the bar fills (0-1). 1 = full width, 0.5 = half width with gaps. */
  fill?: number;
  /** If true, flips the chart horizontally (C shape instead of D) and places legend on the right */
  flip?: boolean;
  /** If true, swaps the position of count and label in the legend */
  swapLabels?: boolean;
  /** Left margin for the chart container (useful when swapLabels causes overflow) */
  marginLeft?: number;
  /** If true, shows active donor count below the outside text on the first (innermost) bar */
  activeDonorShow?: boolean;
  /** The active donor count to display */
  activeDonorNumber?: number;
  /** Bottom position for active donor box (default: 50) */
  activeDonorBottom?: number;
  /** Left offset for active donor box, added to marginLeft (default: -10) */
  activeDonorLeft?: number;
  /** The new donors count to display (yesterday or today based on toggle) */
  newDonorsYesterday?: number;
  /** Label for the new donors section (e.g., "Yesterday's", "Weekend", "Today's") */
  newDonorsLabel?: string;
  /** Whether showing today (true) or yesterday (false) - affects styling */
  showingToday?: boolean;
  /** Callback when the new donors section is clicked to toggle */
  onNewDonorsToggle?: () => void;
  /** Icon to display next to active donor number */
  activeDonorIcon?: ReactNode;
  /** Style for active donor icon */
  activeDonorIconStyle?: React.CSSProperties;
  /** Icon to display next to yesterday number */
  newDonorsYesterdayIcon?: ReactNode;
  /** Style for yesterday icon */
  newDonorsYesterdayIconStyle?: React.CSSProperties;
  /** Box shadow for the chart container */
  boxShadow?: string;
  /** Corner radius for the start end of bars (default: 5) */
  cornerRadius?: number;
  /** Center the component horizontally within its container */
  centered?: boolean;
  /** Configuration for clickable legend items - keys are data item names */
  legendClickConfigs?: Record<string, ILegendClickConfig>;
  /** Handler for clicking the New Donors box (navigates to new donors details) */
  onNewDonorsClick?: () => void;
  /** Tooltip for the New Donors click action */
  newDonorsClickTooltip?: string;
}

/**
 * Custom RadialBar chart component using SVG
 * Renders arcs that open to the right (like a "C" shape)
 * with a large empty center (donut style)
 * Legend items align with their corresponding bars
 */
export const RadialBarChart: FC<IRadialBarChartProps> = ({
  data,
  maxValue,
  height = 250,
  barWidth: barWidthProp,
  gap = 10,
  innerRadius: innerRadiusProp,
  spread: spreadProp,
  fill = 0.5,
  flip = false,
  swapLabels = false,
  marginLeft = 0,
  activeDonorShow = false,
  activeDonorNumber,
  activeDonorBottom = 50,
  activeDonorLeft = -10,
  newDonorsYesterday,
  newDonorsLabel = "Yesterday's",
  showingToday = false,
  onNewDonorsToggle,
  activeDonorIcon,
  activeDonorIconStyle,
  newDonorsYesterdayIcon,
  newDonorsYesterdayIconStyle,
  boxShadow,
  cornerRadius = 5,
  centered = false,
  legendClickConfigs,
  onNewDonorsClick,
  newDonorsClickTooltip,
}) => {
  // Calculate max value from data if not provided (uses barValue if available)
  const max =
    maxValue || Math.max(...data.map((d) => d.barValue ?? d.count)) * 1.25;

  // Reverse data so largest (Year) is at top/outside
  const reversedData = [...data].reverse();

  // Calculate spread and fill-based dimensions
  // spread = total distance between each row center
  // fill = percentage of spread that the bar occupies (0-1)
  const spread = spreadProp ?? (barWidthProp ? barWidthProp + 6 : 28);
  const barWidth = barWidthProp ?? spread * Math.min(1, Math.max(0, fill));

  // Calculate dimensions based on number of bars
  // Using spread for consistent row spacing
  const totalBarsHeight = data.length * spread;

  // If innerRadius is provided, use it; otherwise calculate from height
  // innerRadius is the hole size, outerRadius = innerRadius + totalBarsHeight
  const innerRadius =
    innerRadiusProp ?? Math.max(0, height / 2 - totalBarsHeight);
  const outerRadius = innerRadius + totalBarsHeight;

  // Scale factor to fit within height if outerRadius exceeds height/2
  const maxRadius = height / 2;
  const scale = outerRadius > maxRadius ? maxRadius / outerRadius : 1;
  const _scaledInnerRadius = innerRadius * scale;
  const scaledOuterRadius = outerRadius * scale;
  const scaledBarWidth = barWidth * scale;
  const scaledSpread = spread * scale;

  // Arc opens to the right: starts at top (-90°), sweeps clockwise to bottom (90°)
  const startAngle = -90;
  const endAngle = 70;

  // Center point - vertically centered
  const centerY = height / 2;

  // SVG width needs to accommodate the largest arc (outermost)
  const svgWidth = scaledOuterRadius + scaledBarWidth / 2;

  // Helper to convert polar to cartesian
  const polarToCartesian = (
    cy: number,
    radius: number,
    angleInDegrees: number
  ) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
    return {
      x: radius * Math.cos(angleInRadians),
      y: cy + radius * Math.sin(angleInRadians),
    };
  };

  // Create arc path - draws from startAngle to endAngle
  const describeArc = (
    cy: number,
    radius: number,
    startAngleDeg: number,
    endAngleDeg: number
  ) => {
    const start = polarToCartesian(cy, radius, startAngleDeg);
    const end = polarToCartesian(cy, radius, endAngleDeg);
    const largeArcFlag = Math.abs(endAngleDeg - startAngleDeg) > 180 ? 1 : 0;
    const sweepFlag = 1; // clockwise

    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} ${sweepFlag} ${end.x} ${end.y}`;
  };

  // Create arc with angled/skewed end (10 degree angle on far end only)
  // and rounded corners on the start end
  const describeAngledArc = (
    cy: number,
    radius: number,
    barWidth: number,
    startAngleDeg: number,
    endAngleDeg: number,
    skewAngle = 10
  ) => {
    const innerRadius = radius - barWidth / 2;
    const outerRadius = radius + barWidth / 2;

    // Calculate the angular offset for the skew based on the bar width
    // Only apply to the far end (end of the bar)
    const skewOffset = (skewAngle * barWidth) / (0.8 * radius);

    // Calculate angular offset for corner radius on both edges
    const outerCornerOffset = (cornerRadius / outerRadius) * (180 / Math.PI);
    const innerCornerOffset = (cornerRadius / innerRadius) * (180 / Math.PI);

    // Start points - offset for rounded corners
    const outerStart = polarToCartesian(
      cy,
      outerRadius,
      startAngleDeg + outerCornerOffset
    );
    const innerStart = polarToCartesian(
      cy,
      innerRadius,
      startAngleDeg + innerCornerOffset
    );

    // Corner arc endpoints (at actual start angle but inset radially)
    const outerCornerStart = polarToCartesian(
      cy,
      outerRadius - cornerRadius,
      startAngleDeg
    );
    const innerCornerStart = polarToCartesian(
      cy,
      innerRadius + cornerRadius,
      startAngleDeg
    );

    // End points - with skew (angled end)
    const outerEnd = polarToCartesian(
      cy,
      outerRadius,
      endAngleDeg + skewOffset
    );
    const innerEnd = polarToCartesian(
      cy,
      innerRadius,
      endAngleDeg - skewOffset
    );

    const largeArcFlag = Math.abs(endAngleDeg - startAngleDeg) > 180 ? 1 : 0;

    // Draw: outer start (after corner) -> outer arc -> far end -> inner arc ->
    // inner start (after corner) -> inner corner arc -> outer corner arc -> close
    return `
      M ${outerStart.x} ${outerStart.y}
      A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}
      L ${innerEnd.x} ${innerEnd.y}
      A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}
      A ${cornerRadius} ${cornerRadius} 0 0 1 ${innerCornerStart.x} ${innerCornerStart.y}
      L ${outerCornerStart.x} ${outerCornerStart.y}
      A ${cornerRadius} ${cornerRadius} 0 0 1 ${outerStart.x} ${outerStart.y}
      Z
    `;
  };

  // Calculate radius for a given index (0 = Year = outermost, n-1 = Yesterday = innermost)
  const getRadius = (index: number) => {
    // Index 0 should be outermost (largest radius)
    // Each row is scaledSpread apart
    return scaledOuterRadius - index * scaledSpread - scaledSpread / 2;
  };

  // Calculate the Y position for each legend item
  // The bars are evenly spaced by scaledSpread
  const getBarStartY = (index: number) => {
    // First row center is at scaledSpread/2 from top
    // Each subsequent row is scaledSpread lower
    return scaledSpread / 2 + index * scaledSpread;
  };

  const content = (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        height: height,
        position: 'relative',
        gap: gap,
        marginLeft: marginLeft,
        boxShadow: boxShadow,
      }}
    >
      {/* Legend - each item positioned to align with its bar's start point */}
      {/* Design: {count inside )} —— {( with label inside} */}
      <div
        style={{
          position: 'relative',
          height: height,
          flexShrink: 0,
          width: 190,
          order: flip ? 2 : 1,
        }}
      >
        {reversedData.map((item, index) => {
          const barY = getBarStartY(index);
          // Semicircle radius should be large enough to fit text (at least 14px font height + padding)
          const semicircleRadius = 12;
          const semicircleDiameter = semicircleRadius * 2;
          const lineWidth = 20;
          const tailNotchRadius = semicircleRadius * 0.85;

          // Calculate gap based on the text that appears between the two tails
          const gapText = swapLabels ? item.count.toLocaleString() : item.label;
          const gapTextWidth = measureTextWidth(
            gapText,
            14,
            swapLabels ? 'bold' : 'normal'
          );
          const chartTailGap = gapTextWidth + 10; // text width + 10px padding

          // Legend container is 190px, items start at left: 50 (or 0 when flipped), so connector should span 140px (or 191px when flipped)
          const maxConnectorWidth = flip ? 191 : 140;
          // Minimum widths
          const minChartTailWidth = 8;
          const minTailWidth = 12; // Minimum for rounded tail with notch
          const maxTailWidth = 30; // Preferred width for rounded tail with notch

          // Fixed elements: semicircleRadius(12) + lineWidth(20) + semicircleRadius(12) = 44px
          const fixedWidth = semicircleRadius + lineWidth + semicircleRadius;

          // Available space for: tailWidth + chartTailGap + chartTailWidth
          const availableForTails = maxConnectorWidth - fixedWidth; // 96px

          // Calculate ideal widths
          let tailWidth = maxTailWidth;
          let chartTailWidth = availableForTails - chartTailGap - tailWidth; // 96 - gap - 30 = 66 - gap

          // If chartTailWidth is too small, we need to shrink tailWidth
          if (chartTailWidth < minChartTailWidth) {
            // Set chartTailWidth to minimum and calculate how much tailWidth we can have
            chartTailWidth = minChartTailWidth;
            tailWidth = Math.max(
              minTailWidth,
              availableForTails - chartTailGap - chartTailWidth
            );
          }

          // Total width: chartTail + gap + tail + semicircle + line + semicircle (tails always on chart side)
          const svgConnectorWidth =
            chartTailWidth +
            chartTailGap +
            tailWidth +
            semicircleRadius +
            lineWidth +
            semicircleRadius;
          const svgConnectorHeight = semicircleDiameter;

          return (
            <div
              key={item.name}
              style={{
                position: 'absolute',
                top: barY,
                left: flip ? 0 : 50,
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {/* SVG connector with text inside semicircles */}
              <svg
                width={svgConnectorWidth}
                height={svgConnectorHeight}
                viewBox={`0 0 ${svgConnectorWidth} ${svgConnectorHeight}`}
                style={{
                  flexShrink: 0,
                  overflow: 'visible',
                  transform: flip ? 'scaleX(-1)' : undefined,
                }}
              >
                {/* Left: Simple semicircle */}
                <path
                  d={`M 0 0 A ${semicircleRadius} ${semicircleRadius} 0 0 1 0 ${semicircleDiameter}`}
                  fill="none"
                  stroke={item.color}
                  strokeWidth={2}
                />
                {/* Line */}
                <line
                  x1={semicircleRadius}
                  y1={semicircleRadius}
                  x2={semicircleRadius + lineWidth}
                  y2={semicircleRadius}
                  stroke={item.color}
                  strokeWidth={2}
                />
                {/* Rounded tail with semicircle cutout and notch */}
                <path
                  d={`
                    M ${semicircleRadius + lineWidth} 0
                    A ${semicircleRadius} ${semicircleRadius} 0 0 0 ${
                    semicircleRadius + lineWidth
                  } ${semicircleDiameter}
                    L ${
                      semicircleRadius + lineWidth + tailWidth
                    } ${semicircleDiameter}
                    L ${semicircleRadius + lineWidth + tailWidth} ${
                    semicircleRadius + tailNotchRadius
                  }
                    A ${tailNotchRadius} ${tailNotchRadius} 0 0 1 ${
                    semicircleRadius + lineWidth + tailWidth
                  } ${semicircleRadius - tailNotchRadius}
                    L ${semicircleRadius + lineWidth + tailWidth} 0
                    Z
                  `}
                  fill={item.color}
                />
                {/* Flat tail with notch (chart side) */}
                <path
                  d={`
                    M ${
                      semicircleRadius + lineWidth + tailWidth + chartTailGap
                    } 0
                    L ${svgConnectorWidth} 0
                    L ${svgConnectorWidth} ${semicircleDiameter}
                    L ${
                      semicircleRadius + lineWidth + tailWidth + chartTailGap
                    } ${semicircleDiameter}
                    L ${
                      semicircleRadius + lineWidth + tailWidth + chartTailGap
                    } ${semicircleRadius + tailNotchRadius}
                    A ${tailNotchRadius} ${tailNotchRadius} 0 0 0 ${
                    semicircleRadius + lineWidth + tailWidth + chartTailGap
                  } ${semicircleRadius - tailNotchRadius}
                    Z
                  `}
                  fill={item.color}
                />
                {/* Outside text - count or label depending on swapLabels */}
                <text
                  x={-4}
                  y={semicircleRadius}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill={item.color}
                  fontSize={14}
                  fontWeight={swapLabels ? undefined : 'bold'}
                  style={
                    flip
                      ? {
                          transform: 'scaleX(-1)',
                          transformOrigin: '-4px center',
                        }
                      : undefined
                  }
                >
                  {swapLabels ? item.label : item.count.toLocaleString()}
                </text>
                {/* Gap text - positioned between the two tails */}
                {(() => {
                  const clickConfig = legendClickConfigs?.[item.name];
                  const isClickable = swapLabels && clickConfig; // Only count is clickable
                  const textX =
                    semicircleRadius + lineWidth + tailWidth + chartTailGap / 2;

                  const textElement = (
                    <text
                      x={textX}
                      y={semicircleRadius}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill={item.color}
                      fontSize={14}
                      fontWeight={swapLabels ? 'bold' : undefined}
                      style={{
                        cursor: isClickable ? 'pointer' : undefined,
                        textDecoration: isClickable ? 'underline' : undefined,
                        ...(flip
                          ? {
                              transform: 'scaleX(-1)',
                              transformOrigin: `${textX}px center`,
                            }
                          : {}),
                      }}
                      onClick={
                        isClickable
                          ? (e) => {
                              e.stopPropagation();
                              clickConfig.onClick(item.name);
                            }
                          : undefined
                      }
                    >
                      {swapLabels ? item.count.toLocaleString() : item.label}
                    </text>
                  );

                  if (isClickable && clickConfig) {
                    return (
                      <Tooltip
                        title={clickConfig.tooltip}
                        overlayInnerStyle={{
                          background: '#2f3a45',
                          border: '1px solid #0b345f',
                          borderRadius: 3,
                        }}
                        color="#2f3a45"
                      >
                        {textElement}
                      </Tooltip>
                    );
                  }
                  return textElement;
                })()}
              </svg>
            </div>
          );
        })}
      </div>

      {/* Chart */}
      <svg
        width={svgWidth}
        height={height}
        viewBox={`0 0 ${svgWidth} ${height}`}
        style={{
          flexShrink: 0,
          order: flip ? 1 : 2,
          transform: flip ? 'scaleX(-1)' : undefined,
        }}
      >
        {reversedData.map((item, index) => {
          const radius = getRadius(index);
          const barValue = item.barValue ?? item.count;
          const percentage = Math.min(barValue / max, 1);

          // Don't render bar if value is 0
          if (barValue === 0) {
            return null;
          }

          // Calculate sweep angle based on percentage
          const totalSweep = endAngle - startAngle; // 180 degrees
          const valueSweep = startAngle + totalSweep * percentage;

          // Value arc with angled ends (filled shape)
          const valuePath = describeAngledArc(
            centerY,
            radius,
            scaledBarWidth,
            startAngle,
            valueSweep,
            10 // 10 degree angle on ends
          );

          return (
            <g key={item.name}>
              {/* Value bar */}
              <path d={valuePath} fill={item.color} />
            </g>
          );
        })}
      </svg>

      {/* Metrics box - positioned below the chart */}
      {activeDonorShow && newDonorsYesterday !== undefined && (
        <div
          style={{
            position: 'absolute',
            bottom: activeDonorBottom,
            left: marginLeft + activeDonorLeft,
            display: 'flex',
            gap: 0,
            background: '#cedbef',
            borderRadius: 5,
            boxShadow: '0 3px 7px rgba(0,0,0,0.3)',
            transform: 'skew(-10deg)',
            zIndex: 10,
          }}
        >
          {/* New Donors (Yesterday/Weekend or Today) */}
          {newDonorsYesterday !== undefined && (
            <div
              style={{
                display: 'flex',
                border: `3px solid ${showingToday ? '#52c41a' : '#f31a25'}`,
                borderRadius: 5,
                transition: 'all 0.2s ease',
              }}
            >
              {/* Left side - content */}
              <div
                style={{
                  padding: '12px 10px',
                  textAlign: 'center',
                  flex: 1,
                }}
              >
                <div
                  style={{
                    transform: 'skew(10deg)',
                    color: '#2f3a45',
                    fontSize: 13,
                    fontWeight: 400,
                    marginBottom: 3,
                  }}
                >
                  <span>{newDonorsLabel}</span>
                </div>
                <div
                  style={{
                    transform: 'skew(10deg)',
                    color: showingToday ? '#52c41a' : '#f31a25',
                    fontSize: 28,
                    fontWeight: 800,
                    lineHeight: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  {newDonorsYesterdayIcon && (
                    <span
                      style={{
                        color: showingToday ? '#73d13d' : '#f16a71ff',
                        fontSize: 20,
                        fontWeight: 600,
                      }}
                    >
                      {newDonorsYesterdayIcon}
                    </span>
                  )}
                  {newDonorsYesterday.toLocaleString()}
                </div>
                <div
                  style={{
                    transform: 'skew(10deg)',
                  }}
                >
                  {' '}
                  New Donors
                </div>
              </div>
              {/* Right side - clickable tab link */}
              {onNewDonorsClick && (
                <Tooltip
                  title={newDonorsClickTooltip || 'View new donor list'}
                  overlayInnerStyle={{
                    background: '#2f3a45',
                    border: '1px solid #0b345f',
                    borderRadius: 3,
                  }}
                  color="#2f3a45"
                >
                  <div
                    onClick={onNewDonorsClick}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 8px',
                      borderLeft: `1px solid ${
                        showingToday ? '#52c41a40' : '#f31a2540'
                      }`,
                      cursor: 'pointer',
                      transition: 'background 0.15s ease',
                      background: showingToday ? '#52c41a10' : '#f31a2510',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = showingToday
                        ? '#52c41a25'
                        : '#f31a2525';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = showingToday
                        ? '#52c41a10'
                        : '#f31a2510';
                    }}
                  >
                    <span
                      style={{
                        color: showingToday ? '#52c41a' : '#f31a25',
                        fontSize: 16,
                        fontWeight: 600,
                      }}
                    >
                      →
                    </span>
                  </div>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (centered) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        {content}
      </div>
    );
  }

  return content;
};
